import { Box, Card, CardContent, Grid2 as Grid, Stack, Typography, useMediaQuery } from "@mui/material";
import { getTimestampInLocalTime } from "../../util/dateUtil";
import { openInNewTab } from "../../util/linkUtil";
import { LargeActionButton } from "../Common/LargeActionButton";
import { AlertInformation } from "../AlertInformation/AlertInformation";
import CopyToClipboard from "../CopyToClipboard/CopyToClipboard";

type AsnDetailsCardProps = {
	purchaseOrderNumber: string;
	invoiceNumber?: string;
	carrier?: string;
	dateShipped: string;
	dateDeliveryExpected: string;
	dateDelivered?: string;
	consignmentNoteNumber: string;
	consignmentTrackingUrl?: string;
	consignmentStatus?: string;
	deliveryNoteNumber?: string;
	totalNumberOfUnits: number;
	totalNumberOfPackages: number;
	asnType: string;
	labelId?: string;
    fromLocationId?: string;
	fromLocationName?: string;
    toLocationId?: string;
	toLocationName?: string;
    isLastCard?: boolean;
};

export default function AsnDetailsCard(props: AsnDetailsCardProps) {
	const mobileView = useMediaQuery("(max-width:400px)");
	const {
		purchaseOrderNumber,
		invoiceNumber,
		carrier,
		dateShipped,
		dateDeliveryExpected,
		dateDelivered,
		consignmentNoteNumber,
		consignmentTrackingUrl,
		consignmentStatus,
		deliveryNoteNumber,
		totalNumberOfUnits,
		totalNumberOfPackages,
		asnType,
		labelId,
        fromLocationId,
		fromLocationName,
        toLocationId,
		toLocationName,
        isLastCard,
	} = props;
	const onTrackClick = () => {
		openInNewTab(consignmentTrackingUrl);
	};

	return (
		<Card
			sx={{
				marginTop: 1,
				marginBottom: { xs: isLastCard ? 0.2 : 1 },
			}}
		>
			<CardContent
				sx={{
					padding: { xs: "8px", sm: "8px 16px" },
					"&:last-child": { paddingBottom: { xs: "8px", sm: "8px 16px" } },
				}}
			>
				<Stack spacing={0.5}>
					<Grid container spacing={1}>
						<Grid size={{ xs: 7, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								{asnType === "Transfer" ? "Source ID" : "PO Number"}
							</Typography>
							<Box display="flex" alignItems="center">
								<Typography variant="body2" component="span">
									{purchaseOrderNumber}
								</Typography>
								<CopyToClipboard text={purchaseOrderNumber} />
							</Box>
						</Grid>
						<Grid size={{ xs: 5, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								{asnType === "Transfer" ? "Label ID" : "Invoice Number"}
							</Typography>
							<Box display="flex" alignItems="center">
								<Typography variant="body2" component="span">
									{asnType === "Transfer"
										? labelId
											? labelId
											: "N/A"
										: invoiceNumber
											? invoiceNumber
											: "N/A"}
								</Typography>
								{asnType === "Transfer" && labelId && <CopyToClipboard text={labelId} />}
								{asnType !== "Transfer" && invoiceNumber && <CopyToClipboard text={invoiceNumber} />}
							</Box>
						</Grid>
					</Grid>
					<Grid container spacing={1}>
						<Grid size={{ xs: 7, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								{asnType === "Transfer" ? "From" : "Vendor"}
							</Typography>
							<Typography variant="body2" component="div" noWrap={true}>
								{asnType === "Transfer" ? `${fromLocationId ?? "N/A"} - ${fromLocationName ?? "N/A"}` : fromLocationName ?? "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 5, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								No. Of Items
							</Typography>
							<Typography variant="body2" component="span">
								{totalNumberOfUnits}
							</Typography>
						</Grid>
					</Grid>
					<Grid container spacing={1}>
						<Grid size={{ xs: 7, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								{asnType === "Transfer" ? "To" : "Store"}
							</Typography>
							<Typography variant="body2" component="div" noWrap={true}>
								{toLocationId ? `${toLocationId} - ${toLocationName ?? "N/A"}` : toLocationName ?? "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 5, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								No. Of Packages
							</Typography>
							<Typography variant="body2" component="span">
								{totalNumberOfPackages}
							</Typography>
						</Grid>
					</Grid>
					<Grid container spacing={1}>
						<Grid size={{ xs: 7, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								Carrier
							</Typography>
							<Typography variant="body2" component="div" noWrap={true}>
								{carrier ? carrier : "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 5, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								Date Shipped
							</Typography>
							<Typography variant="body2" component="span">
								{dateShipped ? getTimestampInLocalTime(dateShipped, "EEE dd/MM/yy") : "N/A"}
							</Typography>
						</Grid>
					</Grid>
					<Grid container spacing={1}>
						<Grid size={{ xs: 7, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								Consignment Number
							</Typography>
							<Box display="flex" alignItems="center">
								<Typography variant="body2" component={mobileView ? "div" : "span"} noWrap={true}>
									{consignmentNoteNumber}
								</Typography>
								<CopyToClipboard text={consignmentNoteNumber} />
							</Box>
						</Grid>
						<Grid size={{ xs: 5, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								Date Expected
							</Typography>
							<Typography variant="body2" component="span">
								{getTimestampInLocalTime(dateDeliveryExpected, "EEE dd/MM/yy")}
							</Typography>
						</Grid>
					</Grid>
					<Grid container spacing={1}>
						<Grid size={{ xs: 7, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								Consignment Status
							</Typography>
							<Typography variant="body2" component="div" noWrap={true}>
								{consignmentStatus ? consignmentStatus : "N/A"}
							</Typography>
						</Grid>
						<Grid size={{ xs: 5, md: 6 }}>
							<Typography variant="caption" color="textSecondary" component="div">
								Date Delivered
							</Typography>
							<Typography variant="body2" component="span">
								{dateDelivered ? getTimestampInLocalTime(dateDelivered, "EEE dd/MM/yy") : "N/A"}
							</Typography>
						</Grid>
					</Grid>
					<Box>
						<Typography variant="caption" color="textSecondary" component="div">
							Delivery Number
						</Typography>
						<Box display="flex" alignItems="center">
							<Typography variant="body2" component="span">
								{deliveryNoteNumber ? deliveryNoteNumber : "N/A"}
							</Typography>
							{deliveryNoteNumber && <CopyToClipboard text={deliveryNoteNumber} />}
						</Box>
					</Box>
					{consignmentTrackingUrl ? (
						<Box>
							<LargeActionButton children={"Track"} onClick={() => onTrackClick()} />
						</Box>
					) : (
						<AlertInformation message="No tracking link provided" severity="warning" />
					)}
				</Stack>
			</CardContent>
		</Card>
	);
}
